import type { RepresentativeSafetyCaseOption } from './representativeSafetyCaseSelection';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import { normalizeSafetyReportAreaLabel } from './safetyReportMvpPresentation';
import {
  customerFacingAreaLabel,
  customerFacingCameraLabel,
  customerFacingSafetyRuleLabel,
  resolveCustomerFacingPatternLabel,
} from './workspaceUtils';
import {
  customerScanCameraLabel,
  customerScanZoneLabel,
} from '../../domain/acceptance/safetyScanProductPresentation';
import type { Policy } from '../../services/policiesApi';
import type { Zone } from '../../services/zonesApi';
import { presentAreaLabel } from '../../utils/presentAreaLabel';
import { incidentTypeLabel } from '../incidents/incidentTypeLabels';

export type ReportScopeDisplayLine = {
  label: string;
  value: string;
};

export const SAFETY_POLICY_FIELD_LABEL = 'Safety policy / detection rule';

export const SAFETY_POLICY_FIELD_HELPER =
  'These policies come from the site\'s configured safety rules. They define what the camera system looks for, such as unauthorized entry, forklift-path conflict, or PPE non-compliance.';

export const ALL_CONFIGURED_POLICIES_LABEL = 'All configured policies';

export const CAMERA_FIELD_LABEL = 'Camera';

export const CAMERA_FIELD_HELPER = 'Narrow the Safety Scan to camera observations from a specific camera group.';

export const CAMERA_FIELD_HELPER_SCOPED =
  'Policy and area choices below are limited to the selected camera.';

export const SAFETY_POLICY_FIELD_HELPER_SCOPED =
  'Detection rules deployed on or assigned to the selected camera.';

export const AREA_FIELD_HELPER = 'Narrow the Safety Scan to a specific operating area or zone.';

export const AREA_FIELD_HELPER_SCOPED =
  'Areas configured on the selected camera.';

export const AREA_FIELD_LABEL = 'Area / zone';

export const ALL_AREAS_LABEL = 'All areas';

export const REPORT_SCOPE_PANEL_TITLE = 'Scan Scope';

export const REPORT_SCOPE_HEADING = 'Scan Scope';

export const CHANGE_REPORT_SCOPE_BUTTON = 'Change Scan Scope';

export const HIDE_REPORT_SCOPE_BUTTON = 'Hide Scan Scope Controls';

export const REPORTING_ON_HEADING = 'Reporting on';

export const EVIDENCE_CASE_HEADING = 'Representative evidence';

export const SCOPE_HIERARCHY_NOTE =
  'This Draft Safety Scan packages one recurring risk pattern in the selected time window, not a multi-observation list. One representative evidence sample anchors review and ownership.';

export const SCOPE_HIERARCHY_NOTE_HAZARD_CLASS =
  'This report summarizes recurring risk patterns for the selected risk category in the time window below. It does not claim accident prevention or risk elimination.';

export const REPORT_SCOPE_SUBTITLE_HAZARD_CLASS =
  'This report summarizes recurring risks for one risk category, site, and time window.';

export const REPRESENTATIVE_CASE_HAZARD_CLASS_NOTE =
  'Representative evidence selection applies to recurring-risk-pattern reports only. Risk-category reports aggregate all matching risks in scope.';

export const SCOPE_FROM_REVIEW_NOTE =
  'Using the recurring risk pattern and representative evidence you opened from Review for this Draft Safety Scan.';

export const REPORT_SCOPE_RESOLUTION_HELPER =
  'Check scan scope or technical details.';

export const REPORT_SCOPE_SUBTITLE =
  'This Draft Safety Scan packages the selected recurring risk pattern, representative evidence, and time window.';

export const REPORT_SCOPE_INTRO_COLLAPSED = REPORT_SCOPE_SUBTITLE;

export const REPORT_SCOPE_INTRO_EXPANDED = REPORT_SCOPE_SUBTITLE;

export const CHANGE_SCOPE_INTRO =
  'These controls change what goes into the Scan Package. They do not replace Review. They narrow or shift the recurring risk pattern scope.';

export const ADVANCED_FILTERS_HEADING = 'Change Scan Scope';

export type CollapsedReportScopeSummary = {
  scopeLine: string;
  representativeLine: string;
};

export type ReportScopeHierarchySummary = {
  reportingOnLine: string;
  patternLabel: string;
  cameraLabel: string;
  areaLabel: string;
  timeWindowLabel: string;
  evidenceCaseLine: string;
};

export type CameraSelectOption = {
  value: string;
  label: string;
};

export function resolveCameraFilterSelectValue(
  filter: string,
  options: CameraSelectOption[],
  fallbackLabel?: string | null,
): string {
  const raw = String(filter || '').trim();
  const fallback = String(fallbackLabel || '').trim();
  if (raw) {
    const byValue = options.find((option) => option.value === raw);
    if (byValue) return byValue.value;
    const labelMatches = options.filter((option) => option.label === raw);
    if (labelMatches.length === 1) return labelMatches[0].value;
    if (labelMatches.length > 1) {
      const preferred = labelMatches.find((option) => option.value !== option.label);
      if (preferred) return preferred.value;
      return labelMatches[0].value;
    }
  }
  if (fallback) {
    const byFallbackLabel = options.find((option) => option.label === fallback);
    if (byFallbackLabel) return byFallbackLabel.value;
    const byFallbackValue = options.find((option) => option.value === fallback);
    if (byFallbackValue) return byFallbackValue.value;
    return fallback;
  }
  return raw;
}

export type ReportScopeSelectOption = {
  value: string;
  label: string;
};

export function resolveReportScopePatternLabel(raw: string, reportBuilding: boolean): string {
  const label = customerFacingSafetyRuleLabel(raw);
  const normalized = String(label || '').trim();
  if (normalized && !/^safety event$/i.test(normalized) && !/^current safety (pattern|risk)$/i.test(normalized)) {
    return normalized;
  }
  return reportBuilding ? 'Risk pattern loading' : 'Risk pattern not selected';
}

export function resolveReportScopeCameraLabel(raw: string, reportBuilding: boolean): string {
  const configured = customerScanCameraLabel(raw);
  if (configured) return configured;
  const label = customerFacingCameraLabel(raw);
  if (label && !/^current camera$/i.test(String(raw || '').trim()) && !/^all camera groups$/i.test(label)) {
    return label;
  }
  if (String(raw || '').trim()) return label || String(raw).trim();
  return reportBuilding ? 'Camera group loading' : 'All camera groups';
}

export function resolveReportScopeAreaLabel(raw: string, reportBuilding: boolean): string {
  const configured = customerScanZoneLabel(raw);
  if (configured) return configured;
  const label = normalizeSafetyReportAreaLabel(customerFacingAreaLabel(raw) || raw);
  if (label && label !== 'Area not assigned' && !/^current area$/i.test(String(raw || '').trim())) {
    return label;
  }
  return reportBuilding ? 'Area loading' : 'All areas';
}

function zoneIdsForCamera(zones: Zone[], cameraId: string): Set<string> {
  const camera = String(cameraId || '').trim();
  const ids = new Set<string>();
  if (!camera) return ids;
  for (const zone of zones || []) {
    const zoneId = String(zone?.zone_id || '').trim();
    if (!zoneId) continue;
    if (String(zone.camera_id || '').trim() === camera) ids.add(zoneId);
  }
  return ids;
}

export function policyTargetsCamera(
  policy: Policy,
  cameraId: string,
  zoneIdsOnCamera: Set<string>,
): boolean {
  const camera = String(cameraId || '').trim();
  if (!camera) return true;
  const direct = String(policy.camera_id || policy.target?.camera_id || '').trim();
  if (direct) return direct === camera;
  const scopeCameras = Array.isArray(policy.scope?.camera_ids) ? policy.scope.camera_ids : [];
  if (scopeCameras.some((id) => String(id || '').trim() === camera)) return true;
  const scopeZones = Array.isArray(policy.scope?.zone_ids) ? policy.scope.zone_ids : [];
  if (scopeZones.some((zoneId) => zoneIdsOnCamera.has(String(zoneId || '').trim()))) return true;
  return false;
}

export function buildZoneOptionsForCamera(args: {
  allZones: Zone[];
  cameraId: string;
  incidentZones?: Array<{ zoneId?: string | null; areaLabel?: string | null }>;
}): ReportScopeSelectOption[] {
  const camera = String(args.cameraId || '').trim();
  const merged = new Map<string, ReportScopeSelectOption>();
  if (!camera) {
    for (const zone of args.allZones || []) {
      const value = String(zone?.zone_id || '').trim();
      if (!value) continue;
      merged.set(value, { value, label: presentAreaLabel(zone.name || value) });
    }
  } else {
    for (const zone of args.allZones || []) {
      const value = String(zone?.zone_id || '').trim();
      if (!value) continue;
      if (String(zone.camera_id || '').trim() !== camera) continue;
      merged.set(value, { value, label: presentAreaLabel(zone.name || value) });
    }
    for (const entry of args.incidentZones || []) {
      const value = String(entry.zoneId || '').trim();
      if (!value) continue;
      merged.set(value, {
        value,
        label: presentAreaLabel(entry.areaLabel || value),
      });
    }
  }
  return Array.from(merged.values()).sort((left, right) => left.label.localeCompare(right.label));
}

export function buildPolicyTypeOptionsForCamera(args: {
  allPolicies: Policy[];
  allZones: Zone[];
  cameraId: string;
  incidentPolicyTypes?: Array<{ policyType?: string | null; policyLabel?: string | null }>;
}): ReportScopeSelectOption[] {
  const camera = String(args.cameraId || '').trim();
  const byType = new Map<string, string>();
  if (!camera) {
    for (const policy of args.allPolicies || []) {
      const value = String(policy?.policy_type || '').trim();
      if (!value || byType.has(value)) continue;
      byType.set(value, incidentTypeLabel(value) || value);
    }
  } else {
    const zoneIdsOnCamera = zoneIdsForCamera(args.allZones || [], camera);
    for (const policy of args.allPolicies || []) {
      if (!policyTargetsCamera(policy, camera, zoneIdsOnCamera)) continue;
      const value = String(policy?.policy_type || '').trim();
      if (!value || byType.has(value)) continue;
      const label = String(policy?.name || '').trim();
      byType.set(value, label ? customerFacingSafetyRuleLabel(label) : (incidentTypeLabel(value) || value));
    }
    for (const entry of args.incidentPolicyTypes || []) {
      const value = String(entry.policyType || '').trim();
      if (!value || byType.has(value)) continue;
      const label = String(entry.policyLabel || '').trim();
      byType.set(value, label ? customerFacingSafetyRuleLabel(label) : (incidentTypeLabel(value) || value));
    }
  }
  return Array.from(byType.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function normalizeProofScopePolicyType(
  rawValue: string,
  allPolicies: Policy[],
): string {
  const raw = String(rawValue || '').trim();
  if (!raw) return '';
  const policies = allPolicies || [];
  const byId = policies.find((policy) => String(policy.policy_id || '').trim() === raw);
  if (byId?.policy_type) return String(byId.policy_type).trim();
  const byType = policies.find((policy) => String(policy.policy_type || '').trim() === raw);
  if (byType?.policy_type) return String(byType.policy_type).trim();
  return raw;
}

export function resolveProofScopePolicySelectValue(
  draft: string,
  policyTypeOptions: ReportScopeSelectOption[],
  allPolicies: Policy[],
): string {
  const raw = String(draft || '').trim();
  if (!raw) return '';
  if (policyTypeOptions.some((option) => option.value === raw)) return raw;
  const normalized = normalizeProofScopePolicyType(raw, allPolicies);
  if (policyTypeOptions.some((option) => option.value === normalized)) return normalized;
  return normalized || raw;
}

export function mergeCameraOptionsForScope(
  options: CameraSelectOption[],
  scopeCamera?: { cameraId?: string | null; cameraLabel?: string | null },
): CameraSelectOption[] {
  const merged = new Map<string, CameraSelectOption>();
  options.forEach((option) => {
    const value = String(option.value || '').trim();
    if (!value) return;
    merged.set(value, { value, label: customerFacingCameraLabel(option.label || value) || option.label || value });
  });
  const cameraId = String(scopeCamera?.cameraId || '').trim();
  const cameraLabel = String(scopeCamera?.cameraLabel || '').trim();
  if (cameraId) {
    merged.set(cameraId, { value: cameraId, label: customerFacingCameraLabel(cameraLabel || cameraId) || cameraLabel || cameraId });
  } else if (cameraLabel) {
    merged.set(cameraLabel, { value: cameraLabel, label: customerFacingCameraLabel(cameraLabel) || cameraLabel });
  }
  return Array.from(merged.values());
}

export function buildRepresentativeCaseCollapsedLine(
  option: RepresentativeSafetyCaseOption | null | undefined,
): string {
  if (!option) return 'Representative evidence: Representative evidence not selected';
  const pattern = resolveCustomerFacingPatternLabel({
    preferredLabel: option.patternLabel || option.policyLabel,
    policyTypeId: option.policyType,
  });
  const camera = customerFacingCameraLabel(option.cameraLabel) || 'Camera group not recorded';
  const seen = String(option.seenTimeLabel || '').trim() || 'Time not recorded';
  return `Representative evidence: ${pattern} · ${camera} — ${seen}`;
}

export function buildReportScopeHierarchySummary(args: {
  patternLabel: string;
  cameraLabel: string;
  areaLabel: string;
  startLabel: string;
  endLabel: string;
  representativeCase: RepresentativeSafetyCaseOption | null | undefined;
}): ReportScopeHierarchySummary {
  const pattern = customerFacingSafetyRuleLabel(args.patternLabel) || 'Recurring risk pattern not set';
  const camera = customerScanCameraLabel(args.cameraLabel)
    || customerFacingCameraLabel(args.cameraLabel)
    || (String(args.cameraLabel || '').trim() ? String(args.cameraLabel).trim() : 'All camera groups');
  const area = customerScanZoneLabel(args.areaLabel)
    || normalizeSafetyReportAreaLabel(customerFacingAreaLabel(args.areaLabel) || args.areaLabel);
  const start = String(args.startLabel || '').trim() || 'start not set';
  const end = String(args.endLabel || '').trim() || 'end not set';
  const evidenceCaseLine = buildRepresentativeCaseCollapsedLine(args.representativeCase)
    .replace(/^Representative evidence:\s*/i, '');
  return {
    reportingOnLine: `${pattern} at ${camera} in ${area}, ${start}–${end}`,
    patternLabel: pattern,
    cameraLabel: camera,
    areaLabel: area,
    timeWindowLabel: `${start}–${end}`,
    evidenceCaseLine,
  };
}

export function buildCollapsedReportScopeSummary(args: {
  patternLabel: string;
  cameraLabel: string;
  areaLabel: string;
  startLabel: string;
  endLabel: string;
  representativeCase: RepresentativeSafetyCaseOption | null | undefined;
}): CollapsedReportScopeSummary {
  const hierarchy = buildReportScopeHierarchySummary(args);
  return {
    scopeLine: `${REPORTING_ON_HEADING}: ${hierarchy.reportingOnLine}`,
    representativeLine: `${EVIDENCE_CASE_HEADING}: ${hierarchy.evidenceCaseLine}`,
  };
}

export function buildRepresentativeCaseScopeSummary(
  option: RepresentativeSafetyCaseOption | null | undefined,
): string {
  if (!option) return 'Representative evidence not selected';
  const pattern = resolveCustomerFacingPatternLabel({
    preferredLabel: option.patternLabel || option.policyLabel,
    policyTypeId: option.policyType,
  });
  const camera = customerFacingCameraLabel(option.cameraLabel) || 'Camera group not recorded';
  const seen = String(option.seenTimeLabel || '').trim();
  return seen ? `${pattern} · ${camera} — ${seen}` : `${pattern} · ${camera}`;
}

export function buildHazardClassReportScopeDisplayLines(args: {
  hazardClassDisplayName: string;
  cameraLabel: string;
  areaLabel: string;
  startLabel: string;
  endLabel: string;
}): ReportScopeDisplayLine[] {
  const hazardClass = String(args.hazardClassDisplayName || '').trim() || 'Risk category not set';
  const camera = customerScanCameraLabel(args.cameraLabel)
    || customerFacingCameraLabel(args.cameraLabel)
    || (String(args.cameraLabel || '').trim() ? String(args.cameraLabel).trim() : 'All camera groups');
  const area = customerScanZoneLabel(args.areaLabel)
    || normalizeSafetyReportAreaLabel(customerFacingAreaLabel(args.areaLabel) || args.areaLabel);
  const start = String(args.startLabel || '').trim() || 'start not set';
  const end = String(args.endLabel || '').trim() || 'end not set';
  return [
    { label: displaySafetyManagerLabel('hazard_class'), value: hazardClass },
    { label: 'Camera group', value: camera },
    { label: 'Area', value: area },
    { label: 'Time window', value: `${start} – ${end}` },
    { label: 'Representative evidence', value: 'Not used (risk category report)' },
  ];
}

export function buildReportScopeDisplayLines(args: {
  patternLabel: string;
  cameraLabel: string;
  areaLabel: string;
  startLabel: string;
  endLabel: string;
  representativeCaseSummary: string;
}): ReportScopeDisplayLine[] {
  const pattern = customerFacingSafetyRuleLabel(args.patternLabel) || 'Recurring risk pattern not set';
  const camera = customerScanCameraLabel(args.cameraLabel)
    || customerFacingCameraLabel(args.cameraLabel)
    || (String(args.cameraLabel || '').trim() ? String(args.cameraLabel).trim() : 'All camera groups');
  const area = customerScanZoneLabel(args.areaLabel)
    || normalizeSafetyReportAreaLabel(customerFacingAreaLabel(args.areaLabel) || args.areaLabel);
  const start = String(args.startLabel || '').trim() || 'start not set';
  const end = String(args.endLabel || '').trim() || 'end not set';
  const representative = String(args.representativeCaseSummary || '').trim() || 'Not selected yet';
  return [
    { label: displaySafetyManagerLabel('report_mode_pattern'), value: pattern },
    { label: 'Camera group', value: camera },
    { label: 'Area', value: area },
    { label: 'Time window', value: `${start} – ${end}` },
    { label: 'Representative evidence', value: representative },
  ];
}

export function reportScopeValueNeedsSelection(value: string): boolean {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return true;
  return (
    normalized.includes('not selected')
    || normalized.includes('not set')
    || normalized.includes('not resolved')
    || normalized.includes('not assigned')
  );
}

export function reportScopeLinesAreComplete(lines: ReportScopeDisplayLine[]): boolean {
  return lines.every((line) => !reportScopeValueNeedsSelection(line.value));
}
